import { getRandomItemFromArray } from 'src/replicant/utils/random';
import type { Entity, QueriesObject, QueryResults, System } from '../../odie';
import { BlockComponent } from '../components/BlockComponent';
import { PhaseComponent } from '../components/PhaseComponent';
import { BasicBlockProps, blockPropsMap } from '../defs/block';
import { FirecrackerBoxBlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { ChameleonColor } from '../views/blocks/ChameleonBlockView';
import { FirecrackerBoxBlockComponent } from '../components/FirecrackerBoxBlockComponent';

/*
 * Handles firecracker box block specifics.
 */
export class FirecrackerBoxBlockSystem implements System {
  public static readonly NAME = 'blockFirecrackerBox';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent, FirecrackerBoxBlockComponent],
      added: true,
    },
    phase: {
      components: [PhaseComponent],
      modified: true,
    },
  };

  public scene!: GameScene;
  public queries!: QueryResults;

  public modifiedQuery(
    entity: Entity,
    component: PhaseComponent,
    properties: any,
  ) {
    const phase = this.scene.sessionEntity.c.phase;

    // if move input phase step chameleons
    if (
      properties.phase &&
      component.phase === 'input' &&
      phase.round > 0 &&
      component.moved
    ) {
      this.stepFirecrackerBox();
    }
  }

  public addedToQuery(entity: FirecrackerBoxBlockEntity) {
    const pool = this.getLevelAvailableColors();
    if (!pool) return;

    const comp = entity.c.blockFirecrackerBox;
    const initialColor = comp.activeColor;

    // verify whether initial chameleon colour exists within the spawnables on the level
    const valid = pool.includes(initialColor);

    // If valid, then no need to reassign anything
    if (valid) return;

    // Otherwise, assign a random available colour
    comp.activeColor = getRandomItemFromArray(pool, Math.random);
  }

  private stepFirecrackerBox() {
    const pool = this.getLevelAvailableColors();
    if (!pool) return;

    // rotate colors
    this.queries.block.forEach((entity: FirecrackerBoxBlockEntity) => {
      const currentColor = entity.c.blockFirecrackerBox.activeColor;
      const index = pool.indexOf(currentColor);
      const newColor = pool[(index + 1) % pool.length];
      entity.c.blockFirecrackerBox.activeColor = newColor;
    });
  }

  /**
   * grab the pool of basic blocks from the level spawn list
   */
  private getLevelAvailableColors() {
    return this.scene.mapDef?.spawns
      .filter((id) => id.includes('basic'))
      .map((id) => {
        const props = blockPropsMap[id] as BasicBlockProps;
        return props.color as ChameleonColor;
      });
  }
}
